import { useState, useRef, useEffect, useCallback } from "react";

function parsePos(v) {
  if (!v) return { x: 50, y: 50 };
  const m = String(v).match(/(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!m) return { x: 50, y: 50 };
  return { x: parseFloat(m[1]), y: parseFloat(m[2]) };
}

/**
 * Draggable focal-point picker.
 *  - `src`: image URL
 *  - `value`: current object-position string, e.g. "50% 30%"
 *  - `onChange`: called with the new "X% Y%" string
 *  - `previewAspect`: aspect ratio for the live preview crop (default "9/16" = mobile portrait)
 *  - `label`: optional helper label shown above
 */
export default function FocalPointPicker({ src, value, onChange, previewAspect = "9/16", label }) {
  const [pos, setPos] = useState(parsePos(value));
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => { setPos(parsePos(value)); }, [value]);

  const updateFromEvent = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const point = e.touches?.[0] || e;
    const x = Math.max(0, Math.min(100, ((point.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((point.clientY - rect.top) / rect.height) * 100));
    const next = { x, y };
    setPos(next);
    onChange?.(`${Math.round(x)}% ${Math.round(y)}%`);
  }, [onChange]);

  if (!src) {
    return (
      <div className="p-4 text-center text-xs text-zinc-500 border border-dashed border-white/10 rounded-lg">
        Upload a hero image first to set its mobile focal point.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="focal-point-picker">
      {label && (
        <div className="text-xs text-zinc-400 leading-relaxed">
          {label}
        </div>
      )}
      <div className="grid md:grid-cols-[2fr_1fr] gap-4 items-start">
        {/* Picker — full image with draggable focal dot */}
        <div
          ref={containerRef}
          className={`relative select-none rounded-lg overflow-hidden border border-white/10 bg-black ${dragging ? "cursor-grabbing" : "cursor-crosshair"}`}
          onMouseDown={(e) => { setDragging(true); updateFromEvent(e); }}
          onMouseMove={(e) => { if (dragging) updateFromEvent(e); }}
          onMouseUp={() => setDragging(false)}
          onMouseLeave={() => setDragging(false)}
          onTouchStart={(e) => { setDragging(true); updateFromEvent(e); }}
          onTouchMove={(e) => updateFromEvent(e)}
          onTouchEnd={() => setDragging(false)}
        >
          <img
            src={src}
            alt="Focal point picker"
            className="w-full h-auto block pointer-events-none"
            draggable={false}
          />
          {/* Crosshair guides */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute w-full h-px bg-white/30" style={{ top: `${pos.y}%` }} />
            <div className="absolute h-full w-px bg-white/30" style={{ left: `${pos.x}%` }} />
          </div>
          {/* Focal dot */}
          <div
            className="absolute w-7 h-7 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E63946] border-2 border-white shadow-[0_0_20px_rgba(230,57,70,0.7)] pointer-events-none ring-2 ring-[#E63946]/30"
            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            data-testid="focal-point-dot"
          />
        </div>

        {/* Mobile preview */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-mono">{previewAspect === "9/16" ? "Mobile preview" : "Preview"}</div>
          <div
            className="mx-auto rounded-xl overflow-hidden border border-white/10 bg-black shadow-xl"
            style={{ aspectRatio: previewAspect, maxWidth: 180 }}
          >
            <img
              src={src}
              alt="Mobile preview"
              className="w-full h-full object-cover"
              style={{ objectPosition: `${pos.x}% ${pos.y}%` }}
            />
          </div>
          <div className="mt-3 text-center font-mono text-[10px] text-zinc-500">
            <code className="px-2 py-0.5 bg-[#0A0A0C] border border-white/10 rounded">{Math.round(pos.x)}% {Math.round(pos.y)}%</code>
          </div>
          <button
            type="button"
            onClick={() => { setPos({ x: 50, y: 50 }); onChange?.("50% 50%"); }}
            className="mt-3 w-full text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-white transition-colors"
            data-testid="focal-point-reset"
          >
            Reset to center
          </button>
        </div>
      </div>
    </div>
  );
}
